import React, { useEffect, useMemo, useState } from 'react';
import { useFilterOptions } from './EnhancedFilterForm/useFilterOptions';
import FilterCategorySection from './EnhancedFilterForm/FilterCategorySection';
import UserTypePinnedField from './EnhancedFilterForm/UserTypePinnedField';
import { defaultFilterValue } from '../utils/studentFilterParams';
import { defaultExpandedFilterSections, normalizeFilterUserType } from '../domain/filters/filterProfiles';
import {
  buildFilterFormLayout,
  pruneFiltersForUserType,
  selectedFilterUserType,
} from '../domain/filters/filterFormLayout';

const EnhancedFilterForm = ({
  filters,
  onChange,
  onSubmit,
  loading,
  token,
  compact = false,
  autoApply = false,
}) => {
  const {
    filterFields,
    fieldOptions,
    loadingOptions,
    fetchFieldOptions,
  } = useFilterOptions(token);

  const selectedUserType = selectedFilterUserType(filters);
  const catalogUserType = normalizeFilterUserType(selectedUserType);
  const layout = useMemo(
    () => buildFilterFormLayout(filterFields, selectedUserType),
    [filterFields, selectedUserType]
  );

  const [expandedSections, setExpandedSections] = useState(() =>
    defaultExpandedFilterSections(catalogUserType)
  );

  useEffect(() => {
    setExpandedSections(defaultExpandedFilterSections(catalogUserType));
  }, [catalogUserType]);

  const handleFieldFocus = (fieldName) => {
    if (fieldName && !fieldOptions[fieldName] && !loadingOptions[fieldName]) {
      fetchFieldOptions(fieldName);
    }
  };

  useEffect(() => {
    if (layout.primaryField?.name) {
      handleFieldFocus(layout.primaryField.name);
    }
    // Load static User Type options once the field catalog arrives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout.primaryField?.name]);

  const toggleSection = (category) => {
    setExpandedSections((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const handleRoleChange = (e) => {
    const nextRole = e.target.value;
    const pruned = pruneFiltersForUserType(filters, filterFields, nextRole);
    onChange({ target: { name: 'replace', value: pruned } });
  };

  const handleClearAll = () => {
    onChange({ target: { name: 'clear', value: '' } });
  };

  const fieldsLocked = loading && !autoApply;

  const renderSection = (category) => (
    <FilterCategorySection
      key={category}
      category={category}
      fields={layout.groups[category] || []}
      expanded={Boolean(expandedSections[category])}
      onToggle={toggleSection}
      filters={filters}
      onChange={onChange}
      onFieldFocus={handleFieldFocus}
      loading={fieldsLocked}
      loadingOptions={loadingOptions}
      fieldOptions={fieldOptions}
      compact={compact}
    />
  );

  const primaryOptions =
    fieldOptions[layout.primaryField?.name] || layout.primaryField?.options || [];

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      {layout.primaryField ? (
        <div
          className="border rounded-lg p-4"
          style={{
            borderColor: 'var(--color-border)',
            backgroundColor: 'var(--color-surface)',
          }}
        >
          <UserTypePinnedField
            field={{ ...layout.primaryField, options: primaryOptions }}
            value={filters[layout.primaryField.name] ?? defaultFilterValue(layout.primaryField)}
            onChange={handleRoleChange}
            loading={fieldsLocked}
            isLoading={loadingOptions[layout.primaryField.name]}
          />
          <p className="mt-2 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
            Other filter groups follow this type. They start closed — expand only what you need.
          </p>
        </div>
      ) : null}

      <div className="space-y-4">
        {layout.mainCategories.map(renderSection)}
      </div>

      {layout.additionalCategories.length > 0 ? (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-secondary)' }}>
            Additional filters
          </h3>
          <div className="space-y-4">{layout.additionalCategories.map(renderSection)}</div>
        </div>
      ) : null}

      <div className={compact ? 'flex flex-col gap-2 pt-2' : 'flex gap-4 pt-4'}>
        {!autoApply ? (
          <button
            type="submit"
            disabled={loading}
            className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Filtering...' : 'Apply Filters'}
          </button>
        ) : null}

        <button
          type="button"
          onClick={handleClearAll}
          className="bg-gray-500 text-white px-6 py-2 rounded hover:bg-gray-600 transition-colors"
        >
          Clear All
        </button>
      </div>
    </form>
  );
};

export default EnhancedFilterForm;
